import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faShieldHalved,
  faUser,
  faUserLock,
  faBuilding,
  faMoneyBillTrendUp,
} from "@fortawesome/pro-regular-svg-icons";
import { get as getByKeyPath } from "lodash-es";
import { useFormContext } from "react-hook-form";

import { useCaseTemplateVersion } from "src/cases/queries/templates";
import {
  CaseTemplateFormValues,
  EnumPropertyValueForm,
} from "src/cases/templates/caseTemplateFormUtils";
import { CaseTemplateVersionPublishState } from "src/clients/case-management/api";
import { CaseTemplateVersionPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const AVAILABLE_ICONS: Record<string, IconProp> = {
  "shield-halved": faShieldHalved,
  "user-lock": faUserLock,
  buildings: faBuilding,
  user: faUser,
  "money-bill-trend-up": faMoneyBillTrendUp,
} as const;

export const isValidIcon = (
  icon: string,
): icon is keyof typeof AVAILABLE_ICONS => {
  return AVAILABLE_ICONS[icon] !== undefined;
};

export const getTemplateIcon = (icon: string | null | undefined) => {
  if (!icon || !isValidIcon(icon)) {
    return null;
  }

  return AVAILABLE_ICONS[icon];
};

export const useIsTemplateVersionPublished = () => {
  const { templateId, versionId } =
    useParamsDecode<CaseTemplateVersionPageParams>();
  const { data: version } = useCaseTemplateVersion({ templateId, versionId });

  // Consider version published by default to avoid interface flickering
  return version
    ? version.attributes.publish_state ===
        CaseTemplateVersionPublishState.PUBLISHED
    : true;
};

export type FlattenedStatusValue = {
  _statusKey: string;
  status: string;
  subStatus?: string;
};

export const flattenStatusValues = (
  status?: EnumPropertyValueForm,
): FlattenedStatusValue[] =>
  status?.values.flatMap((value) => {
    if (!value.sub_values?.length) {
      return [
        { status: value.value, _statusKey: buildStatusKey(value._internalId) },
      ];
    }

    return value.sub_values.map((subValue) => ({
      status: value.value,
      subStatus: subValue.value,
      _statusKey: buildStatusKey(value._internalId, subValue._internalId),
    }));
  }) ?? [];

export const getFieldError = (
  form: ReturnType<typeof useFormContext<CaseTemplateFormValues>>,
  keyPath: string,
) => {
  const error = getByKeyPath(form.formState.errors, keyPath);
  return error && typeof error === "object" && "message" in error
    ? error
    : undefined;
};

export const buildStatusLabel = (value: FlattenedStatusValue) =>
  value.subStatus ? `${value.status} • ${value.subStatus}` : value.status;

export const STATUS_DELIMITER = "-";

export const buildBackendStatusKey = (
  value: Omit<FlattenedStatusValue, "_statusKey"> & { _statusKey?: string },
) =>
  value.subStatus
    ? `${value.status}${STATUS_DELIMITER}${value.subStatus}`
    : value.status;

const STATUS_KEY_DELIMITER = "#";
export const buildStatusKey = (
  statusId: string,
  subStatusId?: string | null,
) => {
  if (subStatusId) {
    return `${statusId}${STATUS_KEY_DELIMITER}${subStatusId}`;
  }

  return statusId;
};
